import type { Buffer } from "buffer";
import { postTTSComment, postVoiceComment } from "@api/tracklists";
import Button from "@components/Button";
import Dialog from "@components/Dialog";
import { Column, Row } from "@components/Layout";
import Record from "@components/Record";
import TTSEditor from "@components/TTSEditor";
import type { TTSBlock } from "@components/TTSEditor/types";
import { toast } from "@components/Toaster";
import useI18n from "@contexts/I18nProvider/hooks/useI18n";
import usePlay from "@layouts/App/context/Player/hooks/usePlay";
import { userContext } from "@layouts/App/context/User";
import { ENV } from "@src/env";
import type { ApiError } from "@src/schemas";
import {
	type ParentComponent,
	Show,
	createEffect,
	createMemo,
	createSignal,
	on,
	onCleanup,
	useContext,
} from "solid-js";
import { createStore } from "solid-js/store";
import { SwitchButton } from "./styled";

const AddComment: ParentComponent<{ uuid: string; onAdd: () => void }> = (props) => {
	const [audio, setAudio] = createSignal<Buffer>();
	const [audioUri, setAudioUri] = createSignal<string>();
	const [ttsBlocks, setTTSBlocks] = createStore<TTSBlock[]>([]);
	const [voice, setVoice] = createSignal(true);
	const [loading, setLoading] = createSignal(false);
	const [open, setOpen] = createSignal(false);
	const { user } = useContext(userContext);
	const { t } = useI18n();
	const { playTTSVoiceSample } = usePlay();

	const canSubmit = createMemo(() => {
		const textLength = ttsBlocks.reduce((p, c) => p + c.text.length, 0);
		return (
			(voice() && audio()) ||
			(!voice() && textLength <= ENV.VITE_TTS_COMMENT_MAX_LENGTH && textLength >= ENV.VITE_TTS_COMMENT_MIN_LENGTH)
		);
	});

	const handleSubmitVoiceComment = (b: Buffer) => {
		const formData = new FormData();
		formData.set("file", new Blob([b], { type: "audio/wave" }));
		return postVoiceComment(formData, props.uuid);
	};

	const handleSave = () => {
		if (!canSubmit()) return;
		setLoading(true);
		let p: Promise<object>;
		const a = audio();
		if (a) p = handleSubmitVoiceComment(a);
		else p = postTTSComment(ttsBlocks, props.uuid);
		toast.promise<object, ApiError>(p, {
			loading: t("global.saving.loading"),
			success: () => t("global.saving.success"),
			error: (e) => {
				if (!e) return t("global.saving.error");
				return t(`errors.api.${e.message}`);
			},
		});
		p.then(() => {
			props.onAdd();
			setOpen(false);
		}).finally(() => setLoading(false));
	};

	createEffect(() => {
		const a = audio();
		if (!a) return;
		setAudioUri(URL.createObjectURL(new Blob([a])));
	});

	const handlePlay = () => {
		const uri = audioUri();
		if (!uri) return;
		playTTSVoiceSample(t("app.comment.comment"), uri, uri, user.photo);
	};
	onCleanup(() => URL.revokeObjectURL(audioUri() ?? ""));

	createEffect(
		on(open, (o) => {
			if (o) return;
			setAudio(undefined);
			URL.revokeObjectURL(audioUri() ?? "");
			setVoice(true);
			setTTSBlocks([]);
		}),
	);

	return (
		<Dialog
			openTrigger={props.children}
			buttonsSlot={
				<Button disabled={!canSubmit() || loading()} onclick={handleSave}>
					{t("app.settings.save")}
				</Button>
			}
			open={open()}
			onOpenChange={setOpen}
			triggerW="100%"
		>
			<Column gap="15px">
				<Row>
					<SwitchButton active={voice()} onclick={[setVoice, true]}>
						{t("app.comment.voice")}
					</SwitchButton>
					<SwitchButton active={!voice()} onclick={[setVoice, false]}>
						{t("app.comment.tts")}
					</SwitchButton>
				</Row>
				<Show
					when={voice()}
					fallback={
						<TTSEditor
							blocks={ttsBlocks}
							setTTSBlocks={setTTSBlocks}
							maxLimit={ENV.VITE_TTS_COMMENT_MAX_LENGTH}
							minLimit={ENV.VITE_TTS_COMMENT_MIN_LENGTH}
						/>
					}
				>
					<Column h="50dvh" justifyContent="center">
						<Record
							label={t("app.comment.click")}
							onAudio={setAudio}
							maxLimit={ENV.VITE_COMMENT_MAX_LENGTH}
							minLimit={4000}
							onPlay={audio() ? handlePlay : undefined}
						/>
					</Column>
				</Show>
			</Column>
		</Dialog>
	);
};
export default AddComment;
